import { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSWRConfig } from "swr";
import { Activity, MessageSquare, Pause, Pencil, Play, RefreshCw, Trash2 } from "@digital-twins/design-system/icons";
import {
  SocialPostItem,
  SocialSyncSchedule,
  SocialTopicPostItem,
  SocialTopicPostsSort,
  SocialUniverseChannel,
  SocialUniverseItem,
} from "@digital-twins/shared-types";
import { useCreditInfo } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import { localizedCountryName } from "@/lib/utils/localized-country-name";
import Link from "@/routing/Link";
import { CONTACT_SALES_URL } from "@/pages/SettingsPage/components/billing/planConfig";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SocialTimelineChart } from "./charts/SocialTimelineChart";
import { SocialShareOfVoiceDumbbell } from "./charts/SocialShareOfVoiceDumbbell";
import { SocialActivitySheet } from "./SocialActivitySheet";
import { SocialBoards } from "./SocialBoard";
import { SocialFilterBar } from "./SocialFilterBar";
import { SocialHiddenPosts } from "./SocialHiddenPosts";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialPlayingPostProvider } from "./SocialPlayingPostContext";
import { SocialPostDetail, SocialPostSheet } from "./SocialPostDetail";
import { SocialReasonTooltip } from "./SocialReasonTooltip";
import { SocialTopicCreditsNotice } from "./SocialTopicCreditsNotice";
import { formatSocialCredits, socialMonthLine } from "./socialCredits";
import { socialLimitErrorToast } from "./socialLimitErrors";
import {
  channelStatusKey,
  isChannelCreditPaused,
  socialUpdateNowState,
} from "./socialUpdateNow";
import { useNow } from "./useNow";
import { useRefreshCreditsOnEdge } from "./useRefreshCreditsOnEdge";
import { SocialTopicChannels } from "./SocialTopicChannels";
import { SocialTopicHeaderMeta } from "./SocialTopicHeaderMeta";
import { SocialTopicStatusPill } from "./SocialTopicStatusPill";
import { SocialTopicHighlightProvider } from "./SocialTopicHighlightContext";
import { SocialTopicInsights } from "./SocialTopicInsights";
import { SocialTopicKpis } from "./SocialTopicKpis";
import { SocialTopicOverview } from "./SocialTopicOverview";
import { SocialTopVoices } from "./SocialTopVoices";
import { socialUniverseCardState, socialUniverseSortingLabel } from "./SocialUniverseCard";
import {
  askAboutTopicHref,
  formatSocialDateTime,
  socialAskPostQuestion,
  socialAskTopicQuestion,
  SOCIAL_PLATFORM_LABELS,
  type SocialAskFocus,
} from "./socialFormat";
import {
  channelStatusTone,
  isNotConfiguredReason,
  isTrackerInProgress,
  isTrackerRetryable,
  socialChannelUpdatedAt,
  socialShortRunCopy,
  socialUniverseUpdatedAt,
  trackerStatusLabelKey,
  STATUS_TONE_BADGE_VARIANT,
} from "./socialTrackerStatus";
import { useSocialListeningApi } from "./SocialListeningApiContext";
import { useOverviewCollapsed } from "./useOverviewCollapsed";
import {
  isSocialUniverseCountsKey,
  useSocialUniverse,
  useSocialUniverseCounts,
  useSocialUniverseHiddenPosts,
} from "./useSocialUniverse";
import { refreshSocialPostsUsage } from "./useSocialPostsUsage";
import {
  SocialTopicFiltersUrl,
  UseSocialTopicFiltersResult,
  useSocialTopicFacetCounts,
  useSocialTopicFilters,
} from "./useSocialTopicFilters";
import { registerTopicHotkey, useTopicHotkeys } from "./useTopicHotkeys";

/** What the Topic view hands its panels: one Topic, its live filters and its refresh key. */
export interface SocialTopicSlotContext {
  universe: SocialUniverseItem;
  topicFilters: UseSocialTopicFiltersResult;
  /** Changes when posts land or are hidden, removed or shown again; key every count on it. */
  refreshKey: string | number;
  /** The boards' order: `topicFilters.filters.sort`. */
  sort: SocialTopicPostsSort;
  themeNames: Map<string, string>;
  brandNames: Map<string, string>;
  /** Each brand's stored picture (`SocialBrand.logoPath`); absent or null keeps its color mark. */
  brandLogoPaths?: Map<string, string | null>;
  onOpenPost: (post: SocialTopicPostItem) => void;
  /** Posts hidden here, gone from the boards at once, before the refetch lands. */
  hiddenPostTokens?: ReadonlySet<string>;
}

export interface SocialTopicViewProps {
  universeToken: string;
  /** The Topic list's item, so the view renders before its own fetch returns. */
  fallback: SocialUniverseItem | null;
  canManage: boolean;
  /** False where hiding is not offered (the back office). Defaults to `canManage`. */
  canHidePosts?: boolean;
  /** False outside the main app (the back office has no assistant to ask). */
  canAsk?: boolean;
  showRunDiagnostics?: boolean;
  syncSchedule?: SocialSyncSchedule;
  /** "sheet" inside the back office drawer, "page" on the Topic page. */
  layout?: "sheet" | "page";
  /**
   * Left of the actions in the page layout (the back link). Also rendered
   * above the loading and error states of the page layout, so the Topic is
   * never a dead end.
   */
  headerLeading?: ReactNode;
  /**
   * Extra header actions, before Activity, in the page layout only (the Topic
   * page's Add to collection menu). The sheet layout never renders them, so
   * the back office drawer mounts none of their hooks.
   */
  headerExtras?: ReactNode;
  /** Renders the title; the drawer passes its Sheet title. Defaults to a page heading. */
  renderTitle?: (title: string, className?: string) => ReactNode;
  /**
   * The page URL, where the filters live (the Topic page: shareable, back and
   * forward restore them). Absent keeps them local (the back office Sheet).
   */
  filtersUrl?: SocialTopicFiltersUrl;
  /** Leaves the view (the last channel was removed). */
  onClose: () => void;
  /** Absent where Topics are not edited (the back office). */
  onEdit?: (universe: SocialUniverseItem) => void;
  /** A mutation returned the Topic: the list swaps its item. */
  onUniverseChanged: (universe: SocialUniverseItem) => void;
  /** The last channel was removed, so the Topic is gone. */
  onUniverseDeleted: (token: string) => void;
  /** Posts or channels changed in a way the list should refetch. */
  onRefresh: () => void;
}

type PendingRemoval =
  | { kind: "post"; post: SocialPostItem }
  | { kind: "author"; handle: string; trackerToken: string }
  | { kind: "channel"; channel: SocialUniverseChannel };

type ChannelAction = "sync" | "pause" | "resume";

function errorMessage(error: unknown, fallback: string): string {
  return (
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback
  );
}

/** Long enough to read the toast and reach Undo: the app's default leaves about 4 s. */
const HIDE_UNDO_TOAST_MS = 8000;

/** The shortcuts the help dialog lists: the reduced set (plan D9). */
const SHORTCUT_ROWS = [
  { keys: ["/"], label: "search" },
  { keys: ["m"], label: "metric" },
  { keys: ["o"], label: "overview" },
  { keys: ["j", "k"], label: "posts" },
  { keys: ["Esc"], label: "escape" },
  { keys: ["?"], label: "help" },
] as const;

/**
 * The Topic view (concept §2): the header with the meta row and the
 * sentiment scale, the sticky smart bar, the stat row, the collapsible
 * overview (timeline and highlights; share of voice, channels and top
 * voices), then the theme boards, hidden posts and the channels' status.
 * One filter state drives every panel; on the Topic page it lives in the
 * URL. The Topic page renders it full screen; the back office renders the
 * same body in its drawer, with its admin base, without Ask or Hide.
 * Container queries (1180, 960 and 760 px) set the breakpoints, so the
 * drawer gets the narrow layout.
 */
export function SocialTopicView(props: SocialTopicViewProps) {
  const {
    universeToken,
    fallback,
    canManage,
    canHidePosts = canManage,
    canAsk = true,
    showRunDiagnostics = false,
    syncSchedule = "daily",
    layout = "page",
    headerLeading,
    headerExtras,
    renderTitle,
    filtersUrl,
    onClose,
    onEdit,
    onUniverseChanged,
    onUniverseDeleted,
    onRefresh,
  } = props;

  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const socialListeningApi = useSocialListeningApi();
  const { universe, isLoading, hasError, isBusy, refresh, replace } =
    useSocialUniverse(universeToken, fallback);

  const topicFilters = useSocialTopicFilters(filtersUrl ? { url: filtersUrl } : {});
  const { filters, hasFilters, setFilter, clear: clearFilters } = topicFilters;
  const [selectedPost, setSelectedPost] = useState<SocialTopicPostItem | null>(
    null,
  );
  const [boardPosts, setBoardPosts] = useState<Array<SocialTopicPostItem>>([]);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [isChannelsOpen, setIsChannelsOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(
    null,
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [busyChannel, setBusyChannel] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  // Posts hidden in this view session: the Hidden section's fallback when
  // the server cannot list hidden posts yet.
  const [sessionHidden, setSessionHidden] = useState<
    Array<SocialTopicPostItem>
  >([]);
  const [busyUnhide, setBusyUnhide] = useState<string | null>(null);
  // Hides in flight: the tile stays until the refetch lands, a second click must not hide twice.
  const hidingTokensRef = useRef(new Set<string>());
  const [hiddenPostTokens, setHiddenPostTokens] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const { mutate: mutateCache } = useSWRConfig();
  const overview = useOverviewCollapsed();
  const overviewId = `social-topic-overview-${useId().replace(/:/g, "")}`;
  const { data: creditInfo } = useCreditInfo();
  const isOrgOwner = creditInfo?.isOrgOwner === true;
  // Holds are placed and settled around updates: keep the balance fresh (E1).
  const refreshCredits = useRefreshCreditsOnEdge({ isBusy });
  const [isResumingCredits, setIsResumingCredits] = useState(false);
  // Re-renders when the earliest cooldown or auto-resume window in view ends (E7).
  const now = useNow(
    universe
      ? [
          universe.manualUpdateAvailableAt,
          universe.autoResumeEndsAt,
          ...universe.channels.flatMap((channel) => [
            channel.manualUpdateAvailableAt,
            channel.autoResumeEndsAt,
          ]),
        ]
      : null,
  );

  useTopicHotkeys();

  // The month line reads the Topic: reload it, or it trails the runs the sheet lists.
  const openActivity = () => {
    setIsActivityOpen(true);
    void refresh();
  };

  // Another Topic in the same view: start over. The first render keeps the
  // filters, so a shared link opens with its filters.
  const shownTokenRef = useRef(universeToken);
  useEffect(() => {
    if (shownTokenRef.current === universeToken) {
      return;
    }
    shownTokenRef.current = universeToken;
    clearFilters();
    // Clear keeps the market choice, and that choice belongs to the Topic it was made on.
    setFilter("market", null);
    setSelectedPost(null);
    setIsActivityOpen(false);
    setIsChannelsOpen(false);
    setSessionHidden([]);
    setHiddenPostTokens(new Set<string>());
  }, [universeToken, clearFilters, setFilter]);

  // Settled changes, plus posts accepted while a run is open (D12: results
  // show as they are judged): a run closed (lastSyncedAt), the Topic stopped
  // being busy, posts left waiting were all sorted (the hourly re-judge opens
  // no run), or an open run's accepted posts grew (`progress.accepted`, which
  // moves only when a judge batch accepts posts). Keying on the postsPending
  // count or totalPosts would reload every section on almost every poll.
  const acceptedOpen = universe
    ? universe.channels.reduce((sum, channel) => sum + (channel.progress?.accepted ?? 0), 0)
    : 0;
  const refreshKey = universe
    ? `${universe.lastSyncedAt ?? ""}|${isBusy ? "busy" : "idle"}|${universe.postsPending > 0 ? "sorting" : "sorted"}|${acceptedOpen}|${localRefresh}`
    : localRefresh;
  const token = universe ? universe.token : null;

  const allFilters = useMemo(() => topicFilters.facetFilters(), [topicFilters]);
  const { themeCounts, isLoadingThemes } = useSocialTopicFacetCounts({
    token,
    hasBrands: Boolean(universe && universe.brands.length > 0),
    topicFilters,
    refreshKey,
  });
  // The posts under every filter: the smart bar's live count (the stat row reads the same request).
  const { counts: totals } = useSocialUniverseCounts(
    token,
    { groupBy: "channel", filters: allFilters, includeMarket: true },
    refreshKey,
  );

  const hiddenQuery = useSocialUniverseHiddenPosts(
    canHidePosts ? token : null,
    localRefresh,
  );
  const hiddenPosts = hiddenQuery.isSupported
    ? hiddenQuery.posts
    : sessionHidden;

  const themeNames = useMemo(
    () =>
      new Map((universe?.topics ?? []).map((topic) => [topic.id, topic.name])),
    [universe?.topics],
  );
  const brandNames = useMemo(
    () =>
      new Map((universe?.brands ?? []).map((brand) => [brand.id, brand.name])),
    [universe?.brands],
  );
  const brandLogoPaths = useMemo(
    () =>
      new Map((universe?.brands ?? []).map((brand) => [brand.id, brand.logoPath ?? null])),
    [universe?.brands],
  );

  // Shortcuts: `o` overview, `m` measure, `?` help. `/` is the smart bar's,
  // `j` / `k` the post sheet's, Esc the boards' and the dialogs'.
  const metricRef = useRef(filters.metric);
  metricRef.current = filters.metric;
  useEffect(() => {
    const offOverview = registerTopicHotkey("o", () => overview.toggle());
    const offMetric = registerTopicHotkey("m", () =>
      setFilter("metric", metricRef.current === "reach" ? "posts" : "reach"),
    );
    const offHelp = registerTopicHotkey("?", () => setIsShortcutsOpen((isOpen) => !isOpen));
    return () => {
      offOverview();
      offMetric();
      offHelp();
    };
  }, [overview, setFilter]);

  const stepPost = useCallback(
    (direction: 1 | -1) => {
      setSelectedPost((current) => {
        if (!current || boardPosts.length === 0) {
          return current;
        }
        const index = boardPosts.findIndex((post) => post.token === current.token);
        if (index < 0) {
          return current;
        }
        return boardPosts[(index + direction + boardPosts.length) % boardPosts.length];
      });
    },
    [boardPosts],
  );
  const canStep =
    selectedPost != null && boardPosts.some((post) => post.token === selectedPost.token);

  // The focus an Ask from this screen carries (plan 2026-11-01 D1): the Topic
  // and the filters on screen. Context for the thread, never a restriction.
  function topicAskFocus(item: SocialUniverseItem): SocialAskFocus {
    return {
      topicToken: item.token,
      themeId: filters.themeId,
      brandId: filters.brandId,
      platform: filters.platform,
      sentiment: filters.sentiment,
      source: filters.source,
      window: filters.window,
    };
  }

  function topicAskHref(item: SocialUniverseItem, question?: string): string | null {
    return item.totalPosts > 0 ? askAboutTopicHref(topicAskFocus(item), { question }) : null;
  }

  function askAbout(question: string) {
    if (!universe) {
      return;
    }
    const href = topicAskHref(universe, question);
    if (href) {
      navigate(href);
    }
  }

  // "Ask about this post" (tiles and the post detail): a thread focused on the
  // Topic and the post, started with a question that names the post. Absent
  // where Ask is not offered.
  const askAboutPost = canAsk
    ? (post: SocialPostItem) => {
        if (universe) {
          navigate(
            askAboutTopicHref(
              { topicToken: universe.token, postToken: post.token },
              { question: socialAskPostQuestion(t, post, universe.name) },
            ),
          );
        }
      }
    : undefined;

  function bumpPosts() {
    setLocalRefresh((value) => value + 1);
  }

  /** Typed run-limit toasts (cooldown, queue full, out of credits), else the server message. */
  function showActionError(error: unknown) {
    const limit = socialLimitErrorToast(error, t, {
      isOrgOwner,
      contactSalesUrl: CONTACT_SALES_URL,
      language: i18n.language,
    });
    if (limit) {
      toast.error(limit.message, limit.action ? { action: limit.action } : undefined);
      return;
    }
    toast.error(
      errorMessage(error, t("socialListening.topicToast.actionFailed")),
    );
  }

  async function runChannelAction(
    channel: SocialUniverseChannel,
    action: ChannelAction,
  ) {
    setBusyChannel(channel.trackerToken);
    const platformLabel = SOCIAL_PLATFORM_LABELS[channel.platform];
    try {
      const updated =
        action === "sync"
          ? await socialListeningApi.syncTracker(channel.trackerToken)
          : action === "pause"
            ? await socialListeningApi.pauseTracker(channel.trackerToken)
            : await socialListeningApi.resumeTracker(channel.trackerToken);
      const latest = await refresh();
      if (latest) {
        onUniverseChanged(latest);
      }
      if (action === "sync" && updated.status === "failed") {
        // lastError is English server text naming internals: never shown here;
        // a typed reason has its own words.
        toast.error(
          isNotConfiguredReason(updated.statusReason)
            ? t(`socialListening.statusReason.${updated.statusReason}`)
            : t("socialListening.topicToast.actionFailed"),
        );
        return;
      }
      toast.success(
        action === "sync"
          ? t("socialListening.topicToast.updateStarted")
          : action === "pause"
            ? t("socialListening.topicToast.channelPaused", {
                platform: platformLabel,
              })
            : t("socialListening.topicToast.channelResumed", {
                platform: platformLabel,
              }),
      );
      if (action === "sync") {
        void refreshSocialPostsUsage();
      }
      if (action !== "pause") {
        refreshCredits();
      }
    } catch (error: unknown) {
      showActionError(error);
    } finally {
      setBusyChannel(null);
    }
  }

  /** Banner Resume (S3): resumes every channel paused for credits; the server checks funds. */
  async function handleResumeCredits() {
    if (!universe) {
      return;
    }
    const paused = universe.channels.filter(isChannelCreditPaused);
    if (paused.length === 0) {
      return;
    }
    setIsResumingCredits(true);
    try {
      for (const channel of paused) {
        await socialListeningApi.resumeTracker(channel.trackerToken);
      }
      const latest = await refresh();
      if (latest) {
        onUniverseChanged(latest);
      }
      toast.success(t("socialListening.credits.resumed"));
      refreshCredits();
    } catch (error: unknown) {
      showActionError(error);
      void refresh();
    } finally {
      setIsResumingCredits(false);
    }
  }

  async function handleUpdateAll() {
    if (!universe) {
      return;
    }
    setIsUpdating(true);
    try {
      const updated = await socialListeningApi.syncUniverse(universe.token);
      replace(updated);
      onUniverseChanged(updated);
      toast.success(t("socialListening.topicToast.updateStarted"));
      void refreshSocialPostsUsage();
      refreshCredits();
    } catch (error: unknown) {
      showActionError(error);
    } finally {
      setIsUpdating(false);
    }
  }

  function markPostHidden(postToken: string, isHidden: boolean) {
    setHiddenPostTokens((current) => {
      const next = new Set(current);
      if (isHidden) {
        next.add(postToken);
      } else {
        next.delete(postToken);
      }
      return next;
    });
  }

  async function handleHide(post: SocialTopicPostItem, shouldHide: boolean) {
    if (!universe) {
      return;
    }
    const topicToken = universe.token;
    if (shouldHide && hidingTokensRef.current.has(post.token)) {
      return;
    }
    if (shouldHide) {
      hidingTokensRef.current.add(post.token);
      markPostHidden(post.token, true);
    } else {
      setBusyUnhide(post.token);
    }
    try {
      if (shouldHide) {
        await socialListeningApi.hidePost(post.token);
        const hiddenPost = { ...post, hidden: true };
        setSessionHidden((current) => [
          hiddenPost,
          ...current.filter((item) => item.token !== post.token),
        ]);
        toast.success(
          t("socialListening.topicToast.postHidden", { name: universe.name }),
          {
            action: {
              label: t("socialListening.topicToast.undo"),
              onClick: () => void handleHide(hiddenPost, false),
            },
            duration: HIDE_UNDO_TOAST_MS,
          },
        );
        setSelectedPost(null);
      } else {
        await socialListeningApi.unhidePost(post.token);
        markPostHidden(post.token, false);
        setSessionHidden((current) =>
          current.filter((item) => item.token !== post.token),
        );
        toast.success(
          t("socialListening.topicToast.postShown", { name: universe.name }),
        );
        setSelectedPost((current) =>
          current && current.token === post.token
            ? { ...current, hidden: false }
            : current,
        );
      }
      bumpPosts();
      // Every count of the Topic (chips, themes, sentiment), whatever its filters.
      void mutateCache((key) => isSocialUniverseCountsKey(key, topicToken));
      void refresh();
      onRefresh();
    } catch (error: unknown) {
      if (shouldHide) {
        markPostHidden(post.token, false);
      }
      toast.error(
        errorMessage(error, t("socialListening.topicToast.actionFailed")),
      );
    } finally {
      if (shouldHide) {
        hidingTokensRef.current.delete(post.token);
      } else {
        setBusyUnhide(null);
      }
    }
  }

  async function handleConfirmRemoval() {
    if (!pendingRemoval || !universe) {
      return;
    }
    setIsRemoving(true);
    try {
      if (pendingRemoval.kind === "post") {
        await socialListeningApi.removePost(pendingRemoval.post.token);
        toast.success(t("socialListening.topicToast.postRemoved"));
        setSelectedPost(null);
      } else if (pendingRemoval.kind === "author") {
        await socialListeningApi.removeAuthor(
          pendingRemoval.trackerToken,
          pendingRemoval.handle,
        );
        toast.success(
          t("socialListening.topicToast.authorRemoved", {
            handle: pendingRemoval.handle,
          }),
        );
        setSelectedPost(null);
      } else {
        const { channel } = pendingRemoval;
        await socialListeningApi.removeChannel(channel.trackerToken);
        toast.success(
          t("socialListening.topicToast.channelRemoved", {
            platform: SOCIAL_PLATFORM_LABELS[channel.platform],
          }),
        );
        if (universe.channels.length <= 1) {
          onUniverseDeleted(universe.token);
          setPendingRemoval(null);
          onClose();
          return;
        }
        if (filters.platform === channel.platform) {
          setFilter("platform", null);
        }
      }
      setPendingRemoval(null);
      bumpPosts();
      const latest = await refresh();
      if (latest) {
        onUniverseChanged(latest);
      }
      onRefresh();
    } catch (error: unknown) {
      toast.error(
        errorMessage(error, t("socialListening.topicToast.actionFailed")),
      );
    } finally {
      setIsRemoving(false);
    }
  }

  function renderHeader(item: SocialUniverseItem) {
    // The main Ask opens a thread that asks at once; ThreadsPage and the
    // thread's pending hand-off send it once (a reload or Back never resends).
    const askHref = canAsk
      ? topicAskHref(item, socialAskTopicQuestion(t, topicAskFocus(item), item.name))
      : null;
    const failedChannels = item.channels.filter((channel) =>
      isTrackerRetryable(channel),
    );
    // The Activity footer's "Update all channels now" rules, for the header's "Update now".
    const topicUpdateNow = socialUpdateNowState({
      t,
      lng: i18n.language,
      now,
      isCreditPaused: item.channels.some(isChannelCreditPaused),
      availableAt: item.manualUpdateAvailableAt,
      maxCreditsPerUpdate: item.maxCreditsPerUpdate,
      costKey: "updateAll",
    });
    const isEveryChannelPaused =
      item.channels.length > 0 &&
      item.channels.every((channel) => channel.status === "paused");
    const headerUpdateNow = canManage && item.cadence === "on_demand"
      ? {
          isDisabled:
            isUpdating ||
            busyChannel != null ||
            isEveryChannelPaused ||
            topicUpdateNow.isBlocked,
          reason: topicUpdateNow.reason,
          onUpdate: () => void handleUpdateAll(),
        }
      : null;
    const actionClass =
      "h-[34px] gap-2 rounded-lg px-3 font-medium [&>span]:hidden @[760px]/topic:[&>span]:inline";

    const actions = (
      <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
        {/* The live status leads the actions, before Add to collection. */}
        {/* Below the container breakpoint the status takes its own line, so the icon buttons share one row. */}
        <div className="basis-full @[760px]/topic:basis-auto">
          <SocialTopicStatusPill
            universe={item}
            onOpenActivity={openActivity}
          />
        </div>
        {headerUpdateNow ? (
          <SocialReasonTooltip
            reason={headerUpdateNow.reason}
            isDisabled={headerUpdateNow.isDisabled}
            testId="topic-update-now"
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={actionClass}
              aria-label={t("socialListening.topicCard.updateNow")}
              disabled={headerUpdateNow.isDisabled}
              data-testid="topic-update-now"
              onClick={headerUpdateNow.onUpdate}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              <span>{t("socialListening.topicCard.updateNow")}</span>
            </Button>
          </SocialReasonTooltip>
        ) : null}
        {layout === "page" && headerExtras != null ? headerExtras : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={actionClass}
          aria-label={t("socialListening.topicView.activity")}
          data-testid="social-universe-drawer-activity"
          onClick={openActivity}
        >
          <Activity className="h-4 w-4" aria-hidden="true" />
          <span>{t("socialListening.topicView.activity")}</span>
        </Button>
        {canManage && onEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={actionClass}
            aria-label={t("socialListening.topicView.edit")}
            data-testid="social-universe-drawer-edit"
            onClick={() => onEdit(item)}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            <span>{t("socialListening.topicView.edit")}</span>
          </Button>
        ) : null}
        {askHref ? (
          <Button asChild size="sm" className={actionClass}>
            <Link
              href={askHref}
              aria-label={t("socialListening.topicView.ask")}
              data-testid="social-universe-drawer-ask"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              <span>{t("socialListening.topicView.ask")}</span>
            </Link>
          </Button>
        ) : null}
      </div>
    );
    const status = (
      <>
        {failedChannels.map((channel) => (
          <div
            key={channel.trackerToken}
            className="mt-2 flex flex-wrap items-center gap-2 text-[13px]"
            data-testid={`social-universe-drawer-failed-${channel.platform}`}
          >
            <Text as="span" size="sm" color="muted" className="text-[13px]">
              {t(
                isNotConfiguredReason(channel.statusReason)
                  ? "socialListening.topicView.channelNotConfigured"
                  : item.totalPosts > 0
                    ? "socialListening.topicView.channelFailed"
                    : "socialListening.topicView.channelFailedNoPosts",
                { platform: SOCIAL_PLATFORM_LABELS[channel.platform] },
              )}
            </Text>
            {canManage ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                shape="pill"
                className="h-7 px-3 text-[12px]"
                disabled={busyChannel === channel.trackerToken}
                data-testid={`social-universe-drawer-retry-${channel.platform}`}
                onClick={() => void runChannelAction(channel, "sync")}
              >
                <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
                {t("socialListening.topicView.retry")}
              </Button>
            ) : null}
          </div>
        ))}
        <SocialTopicCreditsNotice
          universe={item}
          now={now}
          isOrgOwner={isOrgOwner}
          canManage={canManage}
          isResuming={isResumingCredits}
          onResume={() => void handleResumeCredits()}
        />
      </>
    );
    const titleClass =
      // A real line height (and an arbitrary size, which sets none): with
      // `leading-none` the display face's glyph box spilled ~46px above the
      // H1 and swallowed clicks on the back link above it.
      "relative min-w-0 font-display text-[36px] font-normal leading-[1.08] tracking-[-0.01em] text-foreground @[760px]/topic:text-[48px] @[760px]/topic:leading-[1.05]";

    return (
      <header className="text-left" data-testid="social-topic-header">
        {/* Wraps at phone width: the actions drop below the back link, never over it. */}
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-x-3 gap-y-2",
            layout === "page" ? "pt-5" : "pr-8",
          )}
          data-testid="social-topic-header-bar"
        >
          {/* Stacked above the title so its hit area can never cover the back link. */}
          <div className="relative z-10 min-w-0">{headerLeading}</div>
          {actions}
        </div>
        <div className="grid grid-cols-1 items-end gap-5 pb-5 pt-5">
          <div className="min-w-0">
            {/* The live status sits with the actions; the line under the title holds facts only. */}
            <div
              className="flex flex-wrap items-center gap-x-4 gap-y-2"
              data-testid="social-topic-title-row"
            >
              {renderTitle ? (
                renderTitle(item.name, titleClass)
              ) : (
                <h1 className={titleClass}>{item.name}</h1>
              )}
            </div>
            <SocialTopicHeaderMeta
              universe={item}
              onOpenChannels={() => setIsChannelsOpen(true)}
            />
            {status}
          </div>
        </div>
      </header>
    );
  }

  function renderBoards(item: SocialUniverseItem) {
    if (isLoadingThemes && !themeCounts) {
      return (
        <div
          className="mt-7 grid grid-cols-1 gap-4 animate-in fade-in fill-mode-both delay-150 @[760px]/topic:grid-cols-2"
          aria-hidden="true"
          data-testid="social-boards-loading"
        >
          {[1, 2].map((index) => (
            <Skeleton
              key={index}
              className="h-[560px] rounded-[calc(var(--radius)+8px)] border border-card-border bg-background"
            />
          ))}
        </div>
      );
    }
    if (!themeCounts || themeCounts.total === 0) {
      if (hasFilters) {
        return (
          <div className="mt-7 space-y-2" data-testid="social-universe-no-matches">
            <Text size="sm" color="muted">
              {t("socialListening.topicView.noMatches")}
            </Text>
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-8 px-3"
              data-testid="social-universe-clear-filters"
              onClick={clearFilters}
            >
              {t("socialListening.topicView.clearFilters")}
            </Button>
          </div>
        );
      }
      // The Topic has posts and the bar with the market filter is on screen: only that filter leaves nothing.
      if (
        themeCounts &&
        filters.market === "inAndUnknown" &&
        item.marketCode &&
        item.totalPosts > 0
      ) {
        return (
          <Text
            size="sm"
            color="muted"
            className="mt-7"
            data-testid="social-universe-local-filter-empty"
          >
            {t("socialListening.topicView.localFilterEmpty", {
              market: localizedCountryName(item.marketCode, i18n.language),
            })}
          </Text>
        );
      }
      const state = socialUniverseCardState(item);
      return (
        <div className="mt-7 space-y-1" data-testid="social-universe-empty">
          {state === "empty" ? (
            <>
              <Text size="sm">{t("socialListening.topicCard.emptyTitle")}</Text>
              <Text size="sm" color="muted">
                {t("socialListening.topicCard.emptyBody")}
              </Text>
              {canManage && onEdit ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  shape="pill"
                  className="mt-2 h-8 px-3"
                  data-testid="social-universe-empty-edit"
                  onClick={() => onEdit(item)}
                >
                  {t("socialListening.topicCard.editTopic")}
                </Button>
              ) : null}
            </>
          ) : (
            <Text size="sm" color="muted">
              {state === "collecting"
                ? t("socialListening.topicCard.collecting", {
                    count: item.totalPosts,
                  })
                : state === "sorting"
                  ? socialUniverseSortingLabel(t, item.postsPending)
                  : t("socialListening.topicCard.notUpdatedYet")}
            </Text>
          )}
        </div>
      );
    }
    if (!slotContext) {
      return null;
    }
    return (
      <SocialBoards
        context={slotContext}
        themeCounts={themeCounts}
        onAskPost={askAboutPost}
        onHidePost={canHidePosts ? (post) => void handleHide(post, true) : undefined}
        onPostsChange={setBoardPosts}
        openPostToken={selectedPost?.token ?? null}
      />
    );
  }

  function renderOverview() {
    if (!slotContext || !universe) {
      return null;
    }
    const hasBrands = universe.brands.length > 0;
    // One channel: its split is the whole topic, already in the stats.
    const hasChannels = universe.channels.length > 1;
    const card = "min-w-0 overflow-hidden rounded-2xl border border-card-border bg-background";
    return (
      <SocialTopicOverview
        id={overviewId}
        isOpen={overview.isOpen}
        main={<SocialTimelineChart context={slotContext} />}
        side={
          hasBrands ? (
            <div className={cn(card, "flex h-full min-h-0 flex-col")} data-testid="social-topic-side-panel">
              <SocialShareOfVoiceDumbbell context={slotContext} />
            </div>
          ) : null
        }
        split={
          <div
            className={cn("grid grid-cols-1 gap-4", hasChannels && "@[760px]/topic:grid-cols-2")}
            data-testid="social-topic-overview-lists"
          >
            {hasChannels ? (
              <div className={card}>
                <SocialTopicChannels context={slotContext} />
              </div>
            ) : null}
            <div className={card}>
              <SocialTopVoices context={slotContext} />
            </div>
          </div>
        }
        footer={
          <SocialTopicInsights context={slotContext} canAsk={canAsk} onAsk={canAsk ? askAbout : undefined} />
        }
      />
    );
  }

  function renderChannels(item: SocialUniverseItem) {
    const monthCredits = item.creditsThisMonth ?? 0;
    return (
      <section className="space-y-2">
        <div className="space-y-0.5">
          <Text
            size="xs"
            color="muted"
            className="tabular-nums"
            data-testid="social-universe-channels-credits"
          >
            {socialMonthLine(
              t,
              i18n.language,
              monthCredits,
              item.postsKeptThisMonth,
            )}
          </Text>
        </div>
        <ul
          className="divide-y divide-border"
          data-testid="social-universe-channels"
        >
          {item.channels.map((channel) => {
            const isPaused = channel.status === "paused";
            const isChannelBusy = busyChannel === channel.trackerToken;
            const lastSynced = formatSocialDateTime(
              socialChannelUpdatedAt(channel),
              i18n.language,
            );
            const channelCredits = channel.creditsThisMonth ?? 0;
            const updateNow = socialUpdateNowState({
              t,
              lng: i18n.language,
              now,
              isCreditPaused: isChannelCreditPaused(channel),
              availableAt: channel.manualUpdateAvailableAt,
              maxCreditsPerUpdate: channel.maxCreditsPerUpdate,
            });
            // A closed run under its target says why (D3).
            const shortRun = channel.lastRun
              ? socialShortRunCopy(channel.lastRun)
              : null;
            const isSyncDisabled =
              isChannelBusy ||
              isPaused ||
              isTrackerInProgress(channel.status) ||
              updateNow.isBlocked;
            return (
              <li
                key={channel.trackerToken}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 text-[13px]"
                data-testid={`social-universe-channel-${channel.platform}`}
              >
                <span className="inline-flex min-w-[110px] items-center gap-1.5 text-foreground">
                  <SocialPlatformIcon
                    platform={channel.platform}
                    className="h-3.5 w-3.5"
                  />
                  {SOCIAL_PLATFORM_LABELS[channel.platform]}
                </span>
                <Badge
                  variant={STATUS_TONE_BADGE_VARIANT[channelStatusTone(channel)]}
                  className="rounded-full px-2 py-0 text-[11px]"
                  data-status={channel.status}
                  data-pause-reason={channel.pauseReason ?? undefined}
                  data-testid={`social-universe-channel-${channel.platform}-status`}
                >
                  {t(
                    // A pause names its reason (credits, maintenance); a partial run waiting to be sorted says so.
                    channel.status === "paused"
                      ? channelStatusKey(channel)
                      : trackerStatusLabelKey(channel),
                  )}
                </Badge>
                <span className="text-muted-foreground">
                  {lastSynced
                    ? t("socialListening.topicCard.updated", {
                        date: lastSynced,
                      })
                    : t("socialListening.topicCard.notUpdatedYet")}
                  {channelCredits > 0 ? (
                    <span
                      className="tabular-nums"
                      data-testid={`social-universe-channel-${channel.platform}-credits`}
                    >
                      {" · "}
                      {t("socialListening.credits.channel.month", {
                        count: channelCredits,
                        value: formatSocialCredits(channelCredits, i18n.language),
                      })}
                    </span>
                  ) : null}
                </span>
                {shortRun ? (
                  <Text
                    as="span"
                    size="xs"
                    color="muted"
                    className="basis-full"
                    data-testid={`social-universe-channel-${channel.platform}-short`}
                  >
                    {t(shortRun.key, shortRun.values)}
                  </Text>
                ) : null}
                {canManage ? (
                  <span className="ml-auto flex flex-wrap items-center gap-1">
                    <SocialReasonTooltip
                      reason={updateNow.reason}
                      isDisabled={isSyncDisabled}
                      testId={`social-universe-channel-${channel.platform}-sync`}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-[12px]"
                        disabled={isSyncDisabled}
                        data-testid={`social-universe-channel-${channel.platform}-sync`}
                        onClick={() => void runChannelAction(channel, "sync")}
                      >
                        <RefreshCw
                          className="mr-1 h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                        {t("socialListening.topicView.updateChannel")}
                      </Button>
                    </SocialReasonTooltip>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-[12px]"
                      disabled={isChannelBusy}
                      data-testid={`social-universe-channel-${channel.platform}-pause`}
                      onClick={() =>
                        void runChannelAction(
                          channel,
                          isPaused ? "resume" : "pause",
                        )
                      }
                    >
                      {isPaused ? (
                        <Play className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <Pause
                          className="mr-1 h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                      )}
                      {isPaused
                        ? t("socialListening.topicView.resumeChannel")
                        : t("socialListening.topicView.pauseChannel")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-[12px] text-destructive hover:text-destructive"
                      disabled={isChannelBusy}
                      aria-label={t(
                        "socialListening.topicView.removeChannelAria",
                        { platform: SOCIAL_PLATFORM_LABELS[channel.platform] },
                      )}
                      data-testid={`social-universe-channel-${channel.platform}-remove`}
                      onClick={() =>
                        setPendingRemoval({ kind: "channel", channel })
                      }
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                      {t("socialListening.topicView.removeChannel")}
                    </Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  const removalTitle =
    pendingRemoval?.kind === "channel"
      ? t("socialListening.topicDialog.removeChannelTitle", {
          platform: SOCIAL_PLATFORM_LABELS[pendingRemoval.channel.platform],
        })
      : pendingRemoval?.kind === "author"
        ? t("socialListening.topicPost.removeAuthorTitle", {
            handle: pendingRemoval.handle,
          })
        : t("socialListening.topicPost.removePostTitle");
  const removalBody =
    pendingRemoval?.kind === "channel"
      ? t("socialListening.topicDialog.removeChannelBody")
      : pendingRemoval?.kind === "author"
        ? t("socialListening.topicPost.removeAuthorBody")
        : t("socialListening.topicPost.removePostBody");

  function renderHiddenTitle(title: string) {
    return renderTitle ? (
      renderTitle(title, "sr-only")
    ) : (
      <Heading level={1} className="sr-only">
        {title}
      </Heading>
    );
  }

  const slotContext: SocialTopicSlotContext | null = universe
    ? {
        universe,
        topicFilters,
        refreshKey,
        sort: filters.sort,
        themeNames,
        brandNames,
        brandLogoPaths,
        onOpenPost: setSelectedPost,
        hiddenPostTokens,
      }
    : null;
  const bodyPadding = layout === "sheet" ? "px-6 pb-6" : "";
  const topicContext = universe
    ? { name: universe.name, topics: universe.topics, brands: universe.brands }
    : null;
  const detailProps = {
    canManage,
    canHide: canHidePosts,
    onHide: () => {
      if (selectedPost) {
        void handleHide(selectedPost, true);
      }
    },
    onUnhide: () => {
      if (selectedPost) {
        void handleHide(selectedPost, false);
      }
    },
    onRemovePost: (post: SocialPostItem) => setPendingRemoval({ kind: "post", post }),
    onRemoveAuthor: (handle: string) => {
      if (selectedPost) {
        setPendingRemoval({
          kind: "author",
          handle,
          trackerToken: selectedPost.trackerToken,
        });
      }
    },
    onStep: canStep ? stepPost : undefined,
    onAsk: askAboutPost,
  };
  // The drawer is already a sheet: its post detail replaces the body.
  const isInlineDetail = layout === "sheet" && selectedPost != null;

  const content = universe && slotContext && topicContext ? (
    <div className={cn("@container/topic", bodyPadding)} data-testid="social-topic-view">
      {renderHeader(universe)}
      {isInlineDetail && selectedPost ? (
        <SocialPostDetail
          {...detailProps}
          post={selectedPost}
          topic={topicContext}
          onBack={() => setSelectedPost(null)}
        />
      ) : (
        <>
          {/* No post was ever collected: filters and a wall of zero stats say nothing, the empty state below does. */}
          {universe.totalPosts > 0 ? (
            <>
              <SocialFilterBar
                context={slotContext}
                postCount={totals ? totals.total : null}
                onShowShortcuts={() => setIsShortcutsOpen(true)}
              />
              <SocialTopicKpis
                context={slotContext}
                overviewId={overviewId}
                isOverviewOpen={overview.isOpen}
                onToggleOverview={overview.toggle}
              />
              {renderOverview()}
            </>
          ) : null}
        </>
      )}
      {/* Every boards state brings its own 28 px top gap: a wrapper margin doubled it under a collapsed overview.
          The boards stay mounted (hidden) behind a post open in place, so they keep the boards they loaded
          and load the open post's neighbours for `j` / `k`. */}
      <div hidden={isInlineDetail} data-testid="social-topic-boards">
        {renderBoards(universe)}
      </div>
      {isInlineDetail || !canHidePosts || hiddenPosts.length === 0 ? null : (
        <div className="mt-12 space-y-8">
          <SocialHiddenPosts
            posts={hiddenPosts}
            topicName={universe.name}
            busyPostToken={busyUnhide}
            onOpenPost={setSelectedPost}
            onUnhide={(post) => void handleHide(post, false)}
            hasMore={hiddenQuery.isSupported && hiddenQuery.hasMore}
            isLoadingMore={hiddenQuery.isLoadingMore}
            onShowMore={hiddenQuery.loadMore}
          />
        </div>
      )}
      {layout === "page" ? (
        <SocialPostSheet
          {...detailProps}
          post={selectedPost}
          topic={topicContext}
          onClose={() => setSelectedPost(null)}
        />
      ) : null}
    </div>
  ) : isLoading ? (
    <div className={cn("space-y-3", bodyPadding)}>
      {layout === "page" ? headerLeading : null}
      {renderHiddenTitle(t("socialListening.tabs.social"))}
      <Skeleton className="h-8 w-1/2 rounded-lg border border-card-border bg-background" />
      <Skeleton className="h-40 rounded-xl border border-card-border bg-background" />
    </div>
  ) : (
    <div className={bodyPadding}>
      {layout === "page" ? headerLeading : null}
      {renderHiddenTitle(t("socialListening.topicView.loadFailed"))}
      <Text size="sm" color="muted" data-testid="social-universe-drawer-error">
        {hasError ? t("socialListening.topicView.loadFailed") : null}
      </Text>
    </div>
  );

  return (
    <SocialTopicHighlightProvider>
      {/* One player at a time across the boards and the post pane (plan D3). */}
      <SocialPlayingPostProvider>{content}</SocialPlayingPostProvider>

      {/* The channel list (status, credits, Update now, Pause, Remove), opened from the header's channels item. */}
      <Sheet open={isChannelsOpen && universe != null} onOpenChange={setIsChannelsOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[720px]"
          aria-describedby={undefined}
          data-testid="topic-channels-sheet"
        >
          {universe ? (
            <>
              <SheetHeader className="border-b border-border p-6 text-left">
                {/* The sheet's semibold reads as a heavy 700 in a regular-only serif: use the heading font's title weight. */}
                <SheetTitle style={{ fontWeight: "var(--font-display-title-weight)" }}>
                  {t("socialListening.topicView.channels")}
                </SheetTitle>
              </SheetHeader>
              <div className="flex-1 p-6">{renderChannels(universe)}</div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <SocialActivitySheet
        universe={universe}
        open={isActivityOpen}
        onOpenChange={setIsActivityOpen}
        canManage={canManage}
        isBusy={isBusy}
        isUpdating={isUpdating || busyChannel != null}
        showRunDiagnostics={showRunDiagnostics}
        canAcceptOffers={canHidePosts}
        onOfferAccepted={() => {
          bumpPosts();
          void mutateCache((key) =>
            isSocialUniverseCountsKey(key, universe?.token ?? ""),
          );
          void refresh();
          void refreshSocialPostsUsage();
          onRefresh();
        }}
        onRetryChannel={(trackerToken) => {
          const channel = universe?.channels.find(
            (item) => item.trackerToken === trackerToken,
          );
          if (channel) {
            void runChannelAction(channel, "sync");
          }
        }}
        onUpdateAll={() => void handleUpdateAll()}
      />

      <Dialog open={isShortcutsOpen} onOpenChange={setIsShortcutsOpen}>
        <DialogContent
          className="w-[min(440px,calc(100vw-32px))] rounded-2xl border-card-border bg-background p-5"
          aria-describedby={undefined}
          data-testid="social-topic-shortcuts"
        >
          <DialogTitle className="text-sm font-semibold">
            {t("socialListening.topicPage.shortcuts.title")}
          </DialogTitle>
          <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
            {SHORTCUT_ROWS.map((row) => (
              <div key={row.label} className="contents">
                <dt className="flex gap-1">
                  {row.keys.map((key) => (
                    <kbd
                      key={key}
                      className="rounded border border-border px-1.5 py-0.5 font-mono text-xs leading-none text-muted-foreground"
                    >
                      {key}
                    </kbd>
                  ))}
                </dt>
                <dd>{t(`socialListening.topicPage.shortcuts.${row.label}`)}</dd>
              </div>
            ))}
          </dl>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingRemoval != null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRemoval(null);
          }
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{removalTitle}</AlertDialogTitle>
            <AlertDialogDescription>{removalBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-full"
              data-testid="social-universe-remove-cancel"
            >
              {t("socialListening.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-destructive text-primary-dark-foreground hover:bg-destructive/90"
              disabled={isRemoving}
              data-testid="social-universe-remove-confirm"
              onClick={(event) => {
                event.preventDefault();
                void handleConfirmRemoval();
              }}
            >
              {pendingRemoval?.kind === "channel"
                ? t("socialListening.topicDialog.removeChannelConfirm")
                : t("socialListening.topicPost.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SocialTopicHighlightProvider>
  );
}
